/**
 * Balli Collection Grid — fixed columns, load-more, scroll-pause auto-load.
 * Mobile: 2 cols · Desktop: 4 cols (default).
 * Re-inits when Shopify facets AJAX replaces #ProductGridContainer.
 */
window.BalliCollectionGrid = {
  init: function (root) {
    if (!root || root.dataset.balliInit === '1') return;
    root.dataset.balliInit = '1';

    var MOBILE_BP = 750;
    var SCROLL_PAUSE_MS = 1000;
    var initialRows = parseInt(root.dataset.initialRows, 10) || 4;
    var loadRows = parseInt(root.dataset.loadRows, 10) || 4;
    var desktopCols = parseInt(root.dataset.defaultCols, 10) || 4;
    var mobileCols = parseInt(root.dataset.mobileCols, 10) || 2;
    var savedRows = parseInt(root.dataset.balliVisibleRows, 10);
    var visibleRows = savedRows >= initialRows ? savedRows : initialRows;
    var autoTimer = null;
    var scrollEndTimer = null;
    var loadMoreObserver = null;

    function isMobile() {
      return window.innerWidth < MOBILE_BP;
    }

    function effectiveCols() {
      return isMobile() ? mobileCols : desktopCols;
    }

    function visibleLimit() {
      return effectiveCols() * visibleRows;
    }

    function persistVisibleRows() {
      root.dataset.balliVisibleRows = String(visibleRows);
    }

    function getGrid() {
      return root.querySelector('[data-balli-grid]');
    }

    function getItems() {
      return Array.prototype.slice.call(root.querySelectorAll('[data-balli-item]'));
    }

    function getLoadBtn() {
      return root.querySelector('[data-balli-load-more]');
    }

    function getCountEl() {
      return root.querySelector('[data-balli-count]');
    }

    function getSentinel() {
      return root.querySelector('[data-balli-sentinel]');
    }

    function applyGridCols() {
      var grid = getGrid();
      if (grid) grid.style.setProperty('--balli-coll-cols', String(effectiveCols()));
    }

    function updateView() {
      var items = getItems();
      var limit = visibleLimit();
      var loadBtn = getLoadBtn();
      var countEl = getCountEl();
      var sentinel = getSentinel();

      items.forEach(function (item, i) {
        item.classList.toggle('is-hidden', i >= limit);
      });

      var hasMore = limit < items.length;
      if (loadBtn) loadBtn.classList.toggle('is-hidden', !hasMore);
      if (sentinel) sentinel.style.display = hasMore ? '' : 'none';
      if (countEl) {
        countEl.textContent = 'Showing ' + Math.min(limit, items.length) + ' of ' + items.length;
      }
    }

    function loadMore() {
      var items = getItems();
      if (visibleLimit() >= items.length) return;
      visibleRows += loadRows;
      persistVisibleRows();
      updateView();
      bindLoadMoreAutoTrigger();
      maybeScheduleDwellLoad();
    }

    function clearDwellTimer() {
      if (autoTimer) {
        window.clearTimeout(autoTimer);
        autoTimer = null;
      }
    }

    function isLoadButtonInView(btn) {
      if (!btn || btn.classList.contains('is-hidden')) return false;
      var rect = btn.getBoundingClientRect();
      return rect.top < window.innerHeight && rect.bottom > 0;
    }

    function maybeScheduleDwellLoad() {
      if (visibleLimit() >= getItems().length) return;
      var btn = getLoadBtn();
      if (!isLoadButtonInView(btn)) return;
      scheduleDwellLoad();
    }

    function scheduleDwellLoad() {
      if (autoTimer || visibleLimit() >= getItems().length) return;
      autoTimer = window.setTimeout(function () {
        autoTimer = null;
        var btn = getLoadBtn();
        if (!isLoadButtonInView(btn)) return;
        loadMore();
      }, SCROLL_PAUSE_MS);
    }

    function bindLoadMoreAutoTrigger() {
      var btn = getLoadBtn();
      if (!btn || !('IntersectionObserver' in window)) return;

      if (loadMoreObserver) loadMoreObserver.disconnect();
      loadMoreObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && !entry.target.classList.contains('is-hidden')) {
            scheduleDwellLoad();
          } else {
            clearDwellTimer();
          }
        });
      }, { threshold: 0.2, rootMargin: '40px 0px' });

      if (!btn.classList.contains('is-hidden')) {
        loadMoreObserver.observe(btn);
      }
    }

    function onScrollEnd() {
      if (scrollEndTimer) window.clearTimeout(scrollEndTimer);
      scrollEndTimer = window.setTimeout(function () {
        scrollEndTimer = null;
        maybeScheduleDwellLoad();
      }, 120);
    }

    function onLoadMoreClick(event) {
      event.preventDefault();
      event.stopPropagation();
      loadMore();
    }

    function onResize() {
      applyGridCols();
      updateView();
    }

    applyGridCols();
    updateView();
    bindLoadMoreAutoTrigger();
    maybeScheduleDwellLoad();
    persistVisibleRows();

    var loadBtn = getLoadBtn();
    if (loadBtn) {
      loadBtn.addEventListener('click', onLoadMoreClick);
    }

    window.addEventListener('scroll', onScrollEnd, { passive: true });
    window.addEventListener('resize', onResize);

    var sortSelect = root.querySelector('[data-balli-sort]');
    if (sortSelect) {
      sortSelect.addEventListener('change', function () {
        var url = new URL(window.location.href);
        url.searchParams.set('sort_by', sortSelect.value);
        window.location.href = url.toString();
      });
    }

    var filterBtn = root.querySelector('[data-balli-open-filters]');
    if (filterBtn) {
      filterBtn.addEventListener('click', function () {
        var drawer = root.querySelector('menu-drawer');
        if (!drawer) return;
        var summary = drawer.querySelector('.mobile-facets__open-wrapper');
        if (summary) summary.click();
      });
    }

    root._balliCollectionTeardown = function () {
      if (loadBtn) loadBtn.removeEventListener('click', onLoadMoreClick);
      window.removeEventListener('scroll', onScrollEnd);
      window.removeEventListener('resize', onResize);
      if (loadMoreObserver) loadMoreObserver.disconnect();
      clearDwellTimer();
      if (scrollEndTimer) window.clearTimeout(scrollEndTimer);
      delete root._balliCollectionTeardown;
    };
  },

  reset: function (root, resetRows) {
    if (!root) return;
    if (typeof root._balliCollectionTeardown === 'function') {
      root._balliCollectionTeardown();
    }
    delete root.dataset.balliInit;
    if (resetRows) delete root.dataset.balliVisibleRows;
  }
};

function isFacetGridRefresh(mutations) {
  return mutations.some(function (mutation) {
    if (mutation.type !== 'childList' || mutation.addedNodes.length === 0) return false;

    for (var i = 0; i < mutation.addedNodes.length; i++) {
      var node = mutation.addedNodes[i];
      if (node.nodeType !== 1) continue;
      if (
        node.id === 'product-grid' ||
        node.classList.contains('balli-coll__footer') ||
        node.classList.contains('collection--empty') ||
        node.querySelector('[data-balli-grid]')
      ) {
        return true;
      }
    }
    return false;
  });
}

function bootBalliCollectionGrid() {
  document.querySelectorAll('.balli-coll').forEach(function (root) {
    BalliCollectionGrid.init(root);
  });

  var container = document.getElementById('ProductGridContainer');
  if (container && !container.dataset.balliObserving) {
    container.dataset.balliObserving = '1';
    var reinitTimer;
    new MutationObserver(function (mutations) {
      if (!isFacetGridRefresh(mutations)) return;
      window.clearTimeout(reinitTimer);
      reinitTimer = window.setTimeout(function () {
        document.querySelectorAll('.balli-coll').forEach(function (root) {
          BalliCollectionGrid.reset(root, true);
          BalliCollectionGrid.init(root);
        });
      }, 80);
    }).observe(container, { childList: true, subtree: true });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootBalliCollectionGrid);
} else {
  bootBalliCollectionGrid();
}

document.addEventListener('shopify:section:load', function (event) {
  var root = event.target.querySelector('.balli-coll');
  if (root) {
    BalliCollectionGrid.reset(root, true);
    BalliCollectionGrid.init(root);
  }
});
